import { call, put, takeLatest, select } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import apiClient from '../services/apiClient';
import { selectAuthUser, selectSelectedOrganization, selectUsers } from '../store/selectors/user';
import {
  fetchOrganizationsPending,
  fetchOrganizationsSuccess,
  fetchUserDataPending,
  fetchUserDataSuccess,
  fetchUsersPending,
  fetchUsersSuccess,
  fetchUserRolesPending,
  fetchUserRolesSuccess,
  createUserPending,
  createUserSuccess,
  deleteUserPending,
  deleteUserSuccess,
  updateUserRolesPending,
  fetchFeatureFlagsPending,
  fetchFeatureFlagsSuccess,
  actionFailed,
  TUpdateUserRoles,
  TDeleteUser,
  TCreateUser,
} from '../store/slices/user';

function* fetchOrganizationsSaga() {
  try {
    const selectedOrganization = yield select(selectSelectedOrganization);

    if (selectedOrganization) {
      const result = yield call(apiClient.get, `/organizations/${selectedOrganization}`, { useOptoUrl: true });

      yield put(fetchOrganizationsSuccess(result));
    }
  } catch (error) {
    yield put(
      actionFailed({
        error,
      }),
    );
  }
}

function* fetchUserDataSaga() {
  try {
    const authUser = yield select(selectAuthUser);

    if (authUser) {
      const result = yield call(
        apiClient.get,
        `/organizations/${authUser.organizationId}/users/${authUser.localAccountId}`,
      );

      yield put(
        fetchUserDataSuccess({
          user: result.data,
        }),
      );
    }
  } catch (error) {
    yield put(
      actionFailed({
        error,
      }),
    );
  }
}

function* fetchUsersSaga() {
  try {
    const selectedOrganization = yield select(selectSelectedOrganization);

    if (selectedOrganization) {
      const result = yield call(apiClient.get, `/organizations/${selectedOrganization}/users`, { useOptoUrl: true });

      yield put(
        fetchUsersSuccess({
          users: result.data,
        }),
      );
    }
  } catch (error) {
    yield put(
      actionFailed({
        error,
      }),
    );
  }
}

function* fetchUserRolesSaga() {
  try {
    const selectedOrganization = yield select(selectSelectedOrganization);

    if (selectedOrganization) {
      const result = yield call(apiClient.get, `/organizations/${selectedOrganization}/roles`, { useOptoUrl: true });

      yield put(
        fetchUserRolesSuccess({
          roles: result.data,
        }),
      );
    }
  } catch (error) {
    yield put(
      actionFailed({
        error,
      }),
    );
  }
}

function* updateUserRoles({ payload }: PayloadAction<TUpdateUserRoles>) {
  try {
    const authUser = yield select(selectAuthUser);
    const selectedOrganization = yield select(selectSelectedOrganization);
    const users = yield select(selectUsers);
    const { displayName, firstName, lastName } = yield users.filter((user) => user.userId === payload.userId)[0];

    if (authUser && selectedOrganization) {
      yield call(
        apiClient.put,
        `/organizations/${selectedOrganization}/users/${payload.userId}`,
        {
          roleIds: payload.roleIds,
          displayName,
          firstName,
          lastName,
        },
        { useOptoUrl: true },
      );

      yield put(fetchUsersPending());
    }
  } catch (error) {
    yield put(
      actionFailed({
        error,
      }),
    );
  }
}

function* createUserSaga({ payload }: PayloadAction<TCreateUser>) {
  try {
    const selectedOrganization = yield select(selectSelectedOrganization);

    if (selectedOrganization) {
      yield call(apiClient.post, `/organizations/${selectedOrganization}/users`, payload);

      yield put(fetchUsersPending());
      yield put(createUserSuccess());
    }
  } catch (error) {
    yield put(
      actionFailed({
        error,
      }),
    );
  }
}

function* deleteUserSaga({ payload }: PayloadAction<TDeleteUser>) {
  try {
    const selectedOrganization = yield select(selectSelectedOrganization);

    if (selectedOrganization) {
      yield call(apiClient.delete, `/organizations/${selectedOrganization}/users/${payload.userId}`);

      yield put(fetchUsersPending());
      yield put(deleteUserSuccess());
    }
  } catch (error) {
    yield put(
      actionFailed({
        error,
      }),
    );
  }
}

function* fetchFeatureFlagsSaga() {
  try {
    const result = yield call(apiClient.get, `/FeatureFlags/enabled`, { useOptoUrl: true });

    yield put(fetchFeatureFlagsSuccess(result));
  } catch (error) {
    yield put(
      actionFailed({
        error,
      }),
    );
  }
}

export default function* watch() {
  yield takeLatest(fetchOrganizationsPending, fetchOrganizationsSaga);
  yield takeLatest(fetchUserDataPending, fetchUserDataSaga);
  yield takeLatest(fetchUsersPending, fetchUsersSaga);
  yield takeLatest(fetchUserRolesPending, fetchUserRolesSaga);
  yield takeLatest(updateUserRolesPending, updateUserRoles);
  yield takeLatest(deleteUserPending, deleteUserSaga);
  yield takeLatest(createUserPending, createUserSaga);
  yield takeLatest(fetchFeatureFlagsPending, fetchFeatureFlagsSaga);
}
